import { gql, useQuery } from "@apollo/client";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Colors, Dialog, Grid, GridColumn, Heading, IconButton, Icons, Logo, MediaQueries, ScrollView, Section, SectionSpacer, Sizes, Spacings, Spans, TextAnchorButton, } from "@polestar/component-warehouse-react";
import React, { useCallback } from "react";
import { marketSelectorQuery } from "../../../data";
import { applyOnNavigate } from "../../../helpers/link-helpers";
import { replaceMarketInUrl } from "../../../helpers/url";
import useMarket from "../../../hooks/useMarket";
import { groupRegions, styleByPipe } from "./MarketSelector.helpers";
export const query = gql `{
  ${marketSelectorQuery()}
}`;
/**
 * To prevent double scrollbars when implementing the market selector
 * See packages/react/src/docs/market-selector.stories.mdx
 * */
const MarketSelector = React.forwardRef(({ visible = false, onClose, ...props }, ref) => {
    const handleClose = useCallback(() => {
        onClose === null || onClose === void 0 ? void 0 : onClose();
    }, [onClose]);
    return (React.createElement(StyledDialog, { ref: ref, open: visible, detached: true, onClose: handleClose },
        React.createElement(SsrMarketSelector, { visible: visible, onClose: onClose, ...props })));
});
export const ConnectedMarketSelector = (props) => {
    // Create an empty apollo options object
    const apolloOptions = {};
    // Set custom client if available
    // Use client from context otherwise
    if (props.client) {
        apolloOptions.client = props.client;
    }
    // Set custom context if available
    if (props.queryContext) {
        apolloOptions.context = props.queryContext;
    }
    const { data, loading, error } = useQuery(query, apolloOptions);
    if (error) {
        console.error(error);
        return null;
    }
    if (loading) {
        return null;
    }
    if (!data) {
        console.error("no market selector data found");
        return null;
    }
    return React.createElement(MarketSelector, { ...data.globalMarketSelector, ...props });
};
export const SsrMarketSelector = ({ title, markets, path, paths, logoProps, visible = false, onClose, onNavigate, onNavigateOptions, closeButtonAriaLabel, }) => {
    // Set default title
    title = title || "Select your region";
    // Get markets from the market provider
    const { availableMarkets, loading, error } = useMarket();
    if (!markets && !loading && !error && (availableMarkets || []).length > 0) {
        markets = availableMarkets;
    }
    const marketsToMenuItems = useCallback((countries) => {
        const links = countries.map(({ marketName, locale }) => ({
            label: marketName,
            locale: locale,
        }));
        return onNavigate
            ? applyOnNavigate(links, onNavigate, onNavigateOptions)
            : links;
    }, [markets]);
    if (!markets) {
        if (error) {
            console.error(error);
            return null;
        }
        if (loading) {
            return null;
        }
        if (!availableMarkets || availableMarkets.length === 0) {
            console.error("Missing markets. Please encapsulate the MarketSelector component with the MarketProvider or pass the markets via props.");
            return null;
        }
    }
    return (React.createElement(StyledWrapper, null,
        React.createElement(Section, null,
            title && (React.createElement(GridColumn, { span: Spans.full },
                React.createElement("header", null,
                    React.createElement(StyledHeading, null, title),
                    React.createElement(SectionSpacer, { size: Sizes.small })))),
            React.createElement(MarketGrid, null, markets &&
                markets
                    .reduce(groupRegions, [])
                    .map(({ name, countries }, index) => {
                    return (React.createElement(StyledRegionGridColumn, { key: index, span: { mobile: 4, desktopWide: 3 }, doubleHeight: countries.length > 5 },
                        name && React.createElement(StyledSubtitle, { as: "h2" }, name),
                        React.createElement("ul", null, marketsToMenuItems(countries).map((item, index) => (React.createElement(StyledLi, { key: index },
                            React.createElement(TextAnchorButton, { tabIndex: visible ? 0 : -1, onClick: item.onClick, href: replaceMarketInUrl(item.locale, (paths && paths[item.locale]) || path || "") }, styleByPipe(item.label))))))));
                })),
            React.createElement(SectionSpacer, { size: Sizes.medium })),
        React.createElement(StyledCloseButtonSection, { id: "test" }, onClose ? (React.createElement(StyledCloseButton, { "aria-label": closeButtonAriaLabel, onClick: onClose, icon: Icons.close, size: Sizes.large })) : (React.createElement(LogoWrapper, null,
            React.createElement(StyledLogo, { ...logoProps, size: Sizes.medium }))))));
};
export const StyledDialog = styled(Dialog) `
  /*
  Set the Dialog "Container" width/height to cover the viewport,
  this is done instead of using the fullscreen prop since it puts padding on the Dialog parent
  */
  min-height: 100vh;
  min-width: 100vw;

  & > div {
    /*
    Set "DialogInner" max-width to none,
    when the prop "fullscreen" isn't used a max-width is placed on "DialogInner"
    */
    max-width: none;
    padding: 0;
  }
`;
const StyledLogo = styled(Logo) `
  line-height: 1;

  > span {
    font-size: 22.3px;

    ${MediaQueries.tablet} {
      font-size: 26px;
    }
  }
`;
const LogoWrapper = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: absolute;
  top: ${Spacings.large};
  right: 0;

  ${MediaQueries.tablet} {
    top: ${Spacings.xLarge};
  }

  ${MediaQueries.desktop} {
    top: ${Spacings.xxLarge};
  }
`;
const StyledWrapper = styled(ScrollView)(css `
  position: relative;
  background-color: white;
  padding-top: ${Spacings.xxxxxLarge};

  ${MediaQueries.tablet} {
    padding-top: ${Spacings.xxxxxLarge};
  }

  ${MediaQueries.desktop} {
    padding-top: ${Spacings.xxxxxxLarge};
  }
`);
const MarketGrid = styled(Grid)(css `
  grid-template-rows: repeat(2, 1fr);
  row-gap: ${Spacings.xxxLarge};

  ${MediaQueries.tablet} {
    grid-auto-flow: column dense;
  }
`);
const StyledRegionGridColumn = styled(GridColumn) `
  grid-row-end: span ${({ doubleHeight }) => (doubleHeight ? 2 : 1)};
`;
const StyledLi = styled.li(css `
  margin-bottom: ${Spacings.medium};
  cursor: pointer;

  &:last-of-type {
    margin-bottom: 0;
  }
`);
const StyledHeading = styled(Heading)(css `
  color: ${Colors.stormGrey};
`);
export const StyledSubtitle = styled(Heading)(css `
  margin-bottom: ${Spacings.large};
  color: ${Colors.black};
`);
const StyledCloseButtonSection = styled(Section)(css `
  position: absolute;
  transform: translateX(-50%);
  left: 50%;
  top: 0;
  display: flex;
  justify-content: flex-end;
`);
export const StyledCloseButton = styled(IconButton)(css `
  position: absolute;
  top: ${Spacings.xxxLarge};
  right: 0;
  padding: 0;

  ${MediaQueries.tablet} {
    margin-top: ${Spacings.xxLarge};
    top: ${Spacings.large};
  }
`);
MarketSelector.displayName = "MarketSelector";
export default MarketSelector;
